<script setup lang="ts">
import { computed, useTemplateRef } from 'vue'

import { RepositoryTimeline } from '@types'

import { cn } from '@ds/lib/utils'
import { buildAxisPeriods } from './utils/helpers'
import { DSSkeleton } from '@ds'

type Props = {
  /** Oldest-first period end dates — the whole range is drawn on the axis at once, nothing scrolls. */
  dates: string[]
  freq: RepositoryTimeline['freq']
  selected?: string
  disabled?: boolean
  loading?: boolean
  /** Ticks only, no month labels — the phone-width layout from the design mock, where labels drop off the axis. */
  dense?: boolean
}

type Emits = {
  select: [date: string]
}

const { dates, freq, selected, disabled = false, loading = false, dense = false } = defineProps<Props>()
const emit = defineEmits<Emits>()

defineOptions({
  name: 'DSPeriodScrollerAxis',
})

const axisRef = useTemplateRef<HTMLDivElement>('axisRef')

const periods = computed(() => buildAxisPeriods(loading ? Array(6).fill(null) : dates, freq))

const endPeriods = computed(() => periods.value.slice(1))

const isEmpty = computed(() => endPeriods.value.length === 0)
const inert = computed(() => disabled || loading || isEmpty.value)

/** 0-based index into `endPeriods` — the design mock's 1-based `sel`, shifted down by one. */
const currentDateIndex = computed(() => endPeriods.value.findIndex(period => period.date === selected))

const periodWidth = computed(() => loading ? 20 : 100 / endPeriods.value.length)
const periodStyle = computed(() => ({
  width: `calc(${periodWidth.value}% + 3px)`,
  left: `calc(${(currentDateIndex.value * periodWidth.value)}% - 3px)`,
}))

/**
 * Every point on the axis — the leading boundary plus one per period —
 * positioned at an even fraction of the width, same as the design mock's
 * `pts` array. The point just before the selected one (`isFrom`) gets its
 * own dot color, same as the mock, so the "from → to" span the highlighted
 * band covers reads clearly even before the label above it is parsed.
 */
const points = computed(() => {
  const total = endPeriods.value.length
  return periods.value.map((period, index) => ({
    ...period,
    isBoundary: index === 0 || index === periods.value.length - 1,
    isSelected: index > 0 && period.date === selected,
    isFrom: index === currentDateIndex.value,
    left: `${total === 0 ? 0 : (index / total) * 100}%`,
  }))
})

/** 1-based, matching the design mock's `sel` — the point that ends the selected period. */
const selectPeriodAt = (index: number) => {
  const clamped = Math.min(endPeriods.value.length, Math.max(1, index))
  const period = endPeriods.value[clamped - 1]
  if (period) emit('select', period.date)
}

const pick = (clientX: number) => {
  const rect = axisRef.value?.getBoundingClientRect()
  if (!rect || !rect.width) return
  const ratio = (clientX - rect.left) / rect.width
  selectPeriodAt(Math.ceil(ratio * endPeriods.value.length) || 1)
}

const handlePointerDown = (event: PointerEvent) => {
  if (inert.value) return
  event.preventDefault()
  axisRef.value?.focus()
  axisRef.value?.setPointerCapture?.(event.pointerId)
  pick(event.clientX)
}

const handlePointerMove = (event: PointerEvent) => {
  if (inert.value || event.buttons === 0) return
  pick(event.clientX)
}

/** The design mock pages by a flat 3 periods regardless of frequency. */
const PAGE_STEP = 3

const handleKeydown = (event: KeyboardEvent) => {
  if (inert.value) return
  const sel = currentDateIndex.value >= 0 ? currentDateIndex.value + 1 : endPeriods.value.length
  const steps: Record<string, number> = {
    ArrowLeft: sel - 1,
    ArrowDown: sel - 1,
    ArrowRight: sel + 1,
    ArrowUp: sel + 1,
    Home: 1,
    End: endPeriods.value.length,
    PageUp: sel - PAGE_STEP,
    PageDown: sel + PAGE_STEP,
  }
  const next = steps[event.key]
  if (next === undefined) return
  event.preventDefault()
  selectPeriodAt(next)
}

const valueText = computed(() => {
  const index = dates.indexOf(selected ?? '')
  if (index <= 0) return ''
  return `${dates[index - 1]} to ${dates[index]}`
})

const valueNow = computed(() => currentDateIndex.value >= 0 ? currentDateIndex.value + 1 : undefined)

const dotClass = (point: { isSelected: boolean; isFrom: boolean }) => cn(
  'absolute bottom-2.75 sm:bottom-px left-[var(--point-left)] -translate-x-1/2 -translate-y-1/2 rounded-full pointer-events-none',
  'transition-[width,height,bottom,background-color] duration-ds-fast',
  point.isSelected
    ? 'size-3 bottom-1.25 sm:-bottom-1.25 bg-primary dark:bg-foreground/80 shadow-ds-focus'
    : point.isFrom
      ? 'size-1.5 bg-primary/50 dark:bg-foreground/50'
      : 'size-1.5 bg-border dark:bg-foreground/25',
)
</script>

<template>
  <div
    ref="axisRef"
    role="slider"
    :tabindex="inert ? -1 : 0"
    aria-label="Selected period"
    :aria-valuemin="endPeriods.length ? 1 : 0"
    :aria-valuemax="endPeriods.length"
    :aria-valuenow="valueNow"
    :aria-valuetext="valueText"
    :aria-disabled="inert"
    class="relative cursor-pointer touch-none select-none rounded-xs focus-visible:outline-4 focus-visible:outline-offset-5 focus-visible:outline-primary/3 focus-visible:dark:outline-foreground/3"
    :class="[dense ? 'h-7.5' : 'h-9', inert && 'cursor-default']"
    @pointerdown="handlePointerDown"
    @pointermove="handlePointerMove"
    @keydown="handleKeydown"
  >
    <div class="absolute bottom-4 sm:bottom-1.5 inset-x-0 h-0.5 bg-primary/16 dark:bg-foreground/16" />
    <div
      v-if="loading"
      class="absolute bottom-2.25 sm:-bottom-1.25 inset-x-0 h-4 sm:h-6 overflow-hidden pointer-events-none"
    >
      <div
        class="period-scroller-scan absolute inset-y-0 left-0 w-1/5 min-w-20 bg-linear-to-r from-transparent via-border/50 to-transparent"
      />
    </div>
    <div
      v-else-if="!isEmpty"
      class="absolute bottom-2.75 sm:bottom-px -translate-y-1/2 h-1.5 rounded-full bg-primary/40 dark:bg-muted-foreground/40 transition-[width,left] duration-ds-fast"
      :style="periodStyle"
    />

    <div
      v-for="(point, index) in points"
      :key="loading ? index : point.date"
      :data-date="point.date"
      :title="point.date"
      :style="{ '--point-left': point.left }"
    >
      <DSSkeleton
        v-if="loading && !dense && !point.isBoundary"
        class="absolute -top-0.5 left-(--point-left) -translate-x-1/2 w-6.5 h-3.5 rounded-lg bg-border"
      />
      <span
        v-else-if="!dense && !point.isBoundary"
        class="absolute top-0 left-(--point-left) -translate-x-1/2 whitespace-nowrap text-[0.6875rem] leading-none z-10"
        :class="point.isSelected ? 'font-bold text-primary dark:text-brand' : 'font-medium text-muted-foreground'"
      >{{ point.label }}</span>

      <span :class="dotClass(point)" />

      <DSSkeleton
        v-if="loading && dense && point.isYearStart"
        class="absolute -bottom-0.5 left-(--point-left) -translate-x-1/2 w-4.5 h-2 rounded-sm bg-border"
      />
      <span
        v-else-if="dense && !loading && point.isYearStart"
        class="absolute -bottom-1 sm:bottom-0 left-(--point-left) -translate-x-1/2 whitespace-nowrap text-[0.59375rem] font-bold leading-none tracking-[0.04em] text-muted-foreground z-10"
      >{{ point.yearShort }}</span>
    </div>
  </div>
</template>

<style scoped>
@keyframes period-scroller-scan {
  from {
    left: 0;
    transform: translateX(0);
  }

  to {
    left: 100%;
    transform: translateX(-100%);
  }
}

.period-scroller-scan {
  @apply animate-[period-scroller-scan_calc(var(--ds-dur-slow)*5)_ease-in-out_infinite_alternate];
}

@media (prefers-reduced-motion: reduce) {
  .period-scroller-scan {
    @apply animate-none;
  }
}
</style>
